import {
  useFetchFrontendCategoriesQuery,
  useFetchFrontendBrandsQuery,
  useFetchHomeFeaturedBannerQuery,
  useFetchHomeFeaturedBlogsQuery,
  useFetchHomeFeaturedProductsQuery,
  useFetchHomeNewArrivalProductsQuery,
  useFetchSlidersQuery,
} from "../../stores/api/pages.api";
import useFrontendMeta from "../../hooks/useFrontendMeta";
import useInView from "../../hooks/useInView";
import { UserLayout } from "./components/layout/UserLayout";
import Img from "./components/Img";
import Slider from "./components/Slider";
import Text from "./components/Text";
import { getImg } from "../../Components/Utils";
import { banner_image_url } from "../../Components/ImageUrls";
import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  ShoppingBag,
  Truck,
} from "lucide-react";
import Container from "./components/Container";
import { ROUTE_PATHS } from "../../constants/routeConstants";
import ProductCard from "./components/cards/ProductCard";
import Effects from "./components/Effects";
import SectionHeading from "./components/SectionHeading";
import BlogCard from "./components/cards/BlogCard";
import ReviewCard from "./components/cards/ReviewCard";
import { useFetchFeaturedReviewsQuery } from "../../stores/api/reviews.api";
import NewsletterModal from "./components/NewsletterModal";

const pad = (n) => String(n).padStart(2, "0");

const HeroTimer = () => {
  const [secs, setSecs] = useState(
    () => Math.floor(Math.random() * 3600) + 1800,
  );
  const ref = useRef(null);
  useEffect(() => {
    ref.current = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(ref.current);
  }, []);
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return (
    <div className="flex w-fit items-center gap-1.5 rounded-lg border border-white/20 bg-black/30 px-3 py-2 backdrop-blur">
      <span className="text-[10px] font-semibold uppercase tracking-widest text-white/60">
        Offer ends in
      </span>
      {[h, m, s].map((v, i) => (
        <span key={i} className="flex items-center gap-1">
          <span className="min-w-[2ch] rounded bg-white/10 px-1.5 py-0.5 text-sm font-bold tabular-nums text-white">
            {pad(v)}
          </span>
          {i < 2 && <span className="text-white/50 font-bold text-sm">:</span>}
        </span>
      ))}
    </div>
  );
};

const Skeleton = ({ className }) => (
  <div className={`animate-pulse rounded-2xl bg-gray-200 ${className}`} />
);

const HomepageSkeleton = () => (
  <UserLayout>
    <div className="mx-auto w-[95%] max-w-[1320px] py-6">
      <Skeleton className="mb-12 h-[420px] w-full lg:h-[620px]" />
      <Skeleton className="mb-6 h-9 w-72" />
      <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[390px]" />
        ))}
      </div>
    </div>
  </UserLayout>
);

const homeContainer = "max-w-[1600px]";

const SLIDE_POSITION_CLASSES = {
  left: "justify-end items-start",
  right: "justify-end items-end text-right",
  center: "justify-center items-center text-center",
  center_top: "justify-start items-center text-center",
  center_bottom: "justify-end items-center text-center",
  left_top: "justify-start items-start",
  left_bottom: "justify-end items-start",
  right_top: "justify-start items-end text-right",
  right_bottom: "justify-end items-end text-right",
};

export const Homepage = () => {
  const { data: slidersData, isLoading: slidersLoading } =
    useFetchSlidersQuery();

  const [productsRef, productsInView] = useInView();
  const [aboutRef, aboutInView] = useInView();
  const [newArrivalsRef, newArrivalsInView] = useInView();
  const [bannerRef, bannerInView] = useInView();
  const [blogsRef, blogsInView] = useInView();
  const [categoriesRef, categoriesInView] = useInView();
  const [brandsRef, brandsInView] = useInView();

  const siteMeta = useFrontendMeta("/");
  const { data: featuredProductsData, isLoading: productsLoading } =
    useFetchHomeFeaturedProductsQuery(undefined, { skip: !productsInView });
  const { data: newArrivalsData, isLoading: newArrivalsLoading } =
    useFetchHomeNewArrivalProductsQuery(undefined, {
      skip: !newArrivalsInView,
    });
  const { data: bannerData, isLoading: bannerLoading } =
    useFetchHomeFeaturedBannerQuery(undefined, { skip: !bannerInView });
  const { data: blogsData, isLoading: blogsLoading } =
    useFetchHomeFeaturedBlogsQuery(undefined, { skip: !blogsInView });
  const { data: categoriesData, isLoading: categoriesLoading } =
    useFetchFrontendCategoriesQuery(undefined, { skip: !categoriesInView });
  const { data: brandsData, isLoading: brandsLoading } =
    useFetchFrontendBrandsQuery(undefined, { skip: !brandsInView });
  const { data: featuredReviews = [] } = useFetchFeaturedReviewsQuery(6);

  if (slidersLoading) return <HomepageSkeleton />;

  const slides = (slidersData ?? []).map((s) => ({
    img: s.image,
    variants: s.image_varients,
    alt: s.image_alt,
    heading: s.title,
    sub: s.short_description,
    cta: s.button_text,
    cta_link: s.link,
    badge: s.badge,
    data_position: s.data_position,
    isEvent: s.type === "event",
  }));
  const products = featuredProductsData ?? [];
  const newArrivals = newArrivalsData ?? [];
  const categories = (categoriesData ?? []).map((c) => ({
    img: c.image,
    label: c.name,
    slug: c.slug,
  }));
  const brands = (brandsData ?? []).map((b) => ({
    img: b.image,
    label: b.name,
    slug: b.slug,
  }));
  const blogs = (blogsData ?? []).map((b) => ({
    title: b.title,
    description: b.short_description,
    img: b.image,
    slug: b.slug,
    category: b.category?.name ?? null,
  }));
  const companyName = siteMeta?.companyName ?? "Ukasha Enterprise";
  const contact = siteMeta?.footer?.contact;

  return (
    <UserLayout>
      <NewsletterModal />
      <section className="pt-2 pb-8">
        <Slider
          className="homepage-hero-slider"
          options={{
            type: "loop",
            gap: "1rem",
            autoplay: true,
            pauseOnHover: true,
            interval: 4500,
            perPage: 1,
            fixedWidth: "80%",
            padding: "0",
            trimSpace: false,
            focus: "center",
            pagination: true,
            breakpoints: {
              992: { fixedWidth: "86%" },
              768: { fixedWidth: "90%" },
              680: { arrows: false },
              576: { fixedWidth: "100%" },
            },
          }}
        >
          {({ SplideSlide }) => (
            <>
              {slides.map((slide, i) => (
                <SplideSlide key={i}>
                  <div className="hero-slide-card relative w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-950">
                    <Img
                      src={getImg(slide.img)}
                      alt={slide.alt}
                      variants={slide.variants}
                      resolveUrl={(p) => `${banner_image_url}${p}`}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out"
                      priority={i === 0}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/35 to-black/10" />
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/65 to-transparent" />

                    <div
                      className={`slide-content relative z-10 flex flex-col gap-2 p-4 text-white sm:gap-4 sm:p-10 lg:p-14 min-h-[380px] sm:min-h-[500px] lg:min-h-[600px] ${SLIDE_POSITION_CLASSES?.[slide.data_position] ?? SLIDE_POSITION_CLASSES.left}`}
                    >
                      <div className="flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur">
                        {/* <span className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <Star
                              key={j}
                              size={13}
                              className="fill-yellow-400 text-yellow-400"
                            />
                          ))}
                        </span> */}
                        {slide.badge || "Premium Picks"}
                      </div>
                      <Text
                        is="h1"
                        className="max-w-2xl text-2xl font-black leading-tight text-white sm:text-3xl md:text-5xl lg:text-6xl"
                      >
                        {slide.heading}
                      </Text>
                      {slide.sub && (
                        <Text
                          is="p"
                          className="max-w-xl text-xs leading-6 text-white/85 sm:text-sm sm:leading-7 md:text-base"
                        >
                          {slide.sub}
                        </Text>
                      )}
                      {!!slide?.isEvent && (

                        <HeroTimer />)
                      }
                      <div className="flex flex-wrap items-center gap-2 pt-1 sm:gap-3 sm:pt-2">
                        <Effects type="crystal">
                          <Text
                            is="a"
                            href={slide.cta_link || ROUTE_PATHS.PRODUCTS}
                            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-gray-950 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-600 hover:text-white sm:px-6 sm:py-3 sm:text-sm"
                            aria-label={slide.cta || "Shop now"}
                          >
                            {slide.cta || "Shop Now"}
                            <ArrowRight size={17} />
                          </Text>
                        </Effects>
                        <Text
                          is="a"
                          href={ROUTE_PATHS.BRANDS}
                          className="inline-flex items-center gap-2 rounded-full border border-white/30 px-4 py-2 text-xs font-semibold text-white backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:bg-white hover:text-gray-950 sm:px-6 sm:py-3 sm:text-sm"
                        >
                          Explore Brands
                        </Text>
                      </div>
                    </div>
                  </div>
                </SplideSlide>
              ))}
            </>
          )}
        </Slider>
      </section>

      <div ref={categoriesRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`py-8 ${homeContainer}`}
          isLoading={categoriesLoading}
          // isVisible={categoriesInView}
          showEmptyState={!categoriesLoading && !categories.length}
          emptyMessage="No categories to show yet."
        >
          <SectionHeading
            id="categories"
            subtitle="Browse our carefully curated categories to find exactly what you need."
            viewMore={ROUTE_PATHS.CATEGORIES}
          >
            Shop by Category
          </SectionHeading>
          <Slider
            className="category-slider"
            options={{
              type: "loop",
              perPage: 5,
              perMove: 1,
              gap: "0.75rem",
              autoplay: false,
              pagination: false,
              arrows: true,
              breakpoints: { 1024: { perPage: 4 }, 640: { perPage: 1 } },
            }}
          >
            {({ SplideSlide }) =>
              categories.map((category) => (
                <SplideSlide key={category.label}>
                  <Text
                    is="a"
                    href={ROUTE_PATHS.CATEGORY_DETAIL.replace(
                      ":categorySlug",
                      category.slug,
                    )}
                    aria-label={category.label}
                    className="group relative block aspect-[1/1] h-[300px] w-full max-w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100"
                  >
                    <Img
                      src={getImg(category.img)}
                      alt={category.label}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60 transition-opacity duration-300 group-hover:opacity-0" />
                    <span className="absolute inset-y-0 left-0 flex w-[74%] -translate-x-full flex-col justify-end bg-slate-950/95 p-6 text-white transition-transform duration-500 ease-out group-hover:translate-x-0">
                      <span className="mb-3 inline-flex w-fit rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white/80">
                        Collection
                      </span>
                      <span className="text-xl font-black leading-tight">
                        {category.label}
                      </span>
                      <span className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold text-gray-950 transition-colors group-hover:bg-blue-600 group-hover:text-white">
                        Explore <ArrowRight size={14} />
                      </span>
                    </span>
                    <span className="absolute bottom-4 left-4 right-4 flex items-center justify-between rounded-full border border-white/20 bg-black/45 px-4 py-2 text-sm font-semibold text-white backdrop-blur transition-all duration-300 group-hover:translate-y-3 group-hover:opacity-0 sm:hidden">
                      {category.label}
                      <ArrowRight size={14} />
                    </span>
                  </Text>
                </SplideSlide>
              ))
            }
          </Slider>
        </Container>
      </Effects>

      <div ref={productsRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`py-12 ${homeContainer}`}
          isLoading={productsLoading}
          // isVisible={productsInView}
          showEmptyState={!productsLoading && !products.length}
          emptyMessage="No products to show yet."
        >
          <SectionHeading
            id="featured-products"
            subtitle="Curated bestsellers with premium quality and quick inquiry."
            viewMore={ROUTE_PATHS.PRODUCTS}
          >
            Featured Products
          </SectionHeading>

          <Slider
            className="premium-product-slider"
            options={{
              type: "loop",
              perPage: 4,
              perMove: 1,
              gap: "1.5rem",
              autoplay: false,
              pagination: false,
              breakpoints: {
                1180: { perPage: 3 },
                768: { perPage: 2 },
                520: { perPage: 1, arrows: false, pagination: true },
              },
            }}
          >
            {({ SplideSlide }) => (
              <>
                {products.map((product, i) => (
                  <SplideSlide key={product.id ?? i}>
                    <ProductCard product={product} />
                  </SplideSlide>
                ))}
              </>
            )}
          </Slider>
        </Container>
      </Effects>

      <div ref={brandsRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`pb-6 ${homeContainer}`}
          isLoading={brandsLoading}
          // isVisible={brandsInView}
          showEmptyState={!brandsLoading && !brands.length}
          emptyMessage="No brands to show yet."
        >
          <Slider
            className="brand-slider"
            options={{
              type: "loop",
              perPage: 6,
              perMove: 1,
              gap: "0.75rem",
              autoplay: true,
              interval: 1000,
              pauseOnHover: true,
              pagination: false,
              arrows: false,
              breakpoints: {
                1180: { perPage: 5 },
                900: { perPage: 4 },
                640: { perPage: 2 },
              },
            }}
          >
            {({ SplideSlide }) =>
              brands.map((brand) => (
                <SplideSlide key={brand.slug ?? brand.label}>
                  <Text
                    is="a"
                    href={`${ROUTE_PATHS.PRODUCTS_BY_BRAND.replace(':brandSlug',brand.slug)}`}
                    aria-label={brand.label}
                    className="flex h-28 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white"
                  >
                    <Img
                      src={getImg(brand.img)}
                      alt={brand.label}
                      className="h-full w-full object-contain"
                    />
                  </Text>
                </SplideSlide>
              ))
            }
          </Slider>
        </Container>
      </Effects>

      <div ref={aboutRef} />
      {(siteMeta || aboutInView) && (
        <Effects type="scroll-fadeup">
          <section className="bg-slate-950 py-12">
            <Container className={homeContainer}>
              <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <div>
                  <Text
                    is="span"
                    className="mb-4 inline-flex rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-100"
                  >
                    About {companyName}
                  </Text>
                  <Text
                    is="h2"
                    className="max-w-2xl text-3xl font-black leading-tight text-white md:text-5xl"
                  >
                    Built for customers who want quality, clarity, and a smooth
                    buying experience.
                  </Text>
                  {siteMeta?.footer?.description && (
                    <Text className="mt-5 max-w-2xl text-sm leading-7 text-slate-300 md:text-base">
                      {siteMeta.footer.description}
                    </Text>
                  )}

                  <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {[
                      {
                        icon: <CheckCircle size={20} />,
                        title: "Verified Quality",
                        desc: "Carefully selected products and dependable sourcing.",
                      },
                      {
                        icon: <ShieldCheck size={20} />,
                        title: "Secure Inquiry",
                        desc: "Simple product flow with direct support before purchase.",
                      },
                      {
                        icon: <Truck size={20} />,
                        title: "Fast Response",
                        desc: "Quick availability checks and order coordination.",
                      },
                    ].map((item) => (
                      <div
                        key={item.title}
                        className="rounded-xl border border-white/10 bg-white/[0.06] p-5 text-white"
                      >
                        <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white">
                          {item.icon}
                        </div>
                        <Text is="h3" className="text-sm font-bold text-white">
                          {item.title}
                        </Text>
                        <Text className="mt-2 text-xs leading-5 text-slate-300">
                          {item.desc}
                        </Text>
                      </div>
                    ))}
                  </div>

                  <Text
                    is="a"
                    href={ROUTE_PATHS.ABOUT}
                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-gray-950 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-500 hover:text-white"
                  >
                    Learn More <ArrowRight size={16} />
                  </Text>
                </div>

                <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.08] p-4">
                  {siteMeta?.home_about_image ? (
                    <Img
                      src={siteMeta.home_about_image}
                      alt={companyName}
                      className="h-full w-full rounded-2xl bg-white object-cover p-2"
                    />
                  ) : (
                    <div className="flex aspect-[4/3] items-center justify-center rounded-2xl bg-white/10 text-2xl font-black text-white">
                      {companyName}
                    </div>
                  )}

                  {contact && (
                    <div className="mt-4 grid grid-cols-1 gap-3">
                      {contact.address && (
                        <div className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 text-sm text-gray-600">
                          <MapPin
                            size={17}
                            className="mt-0.5 flex-shrink-0 text-blue-600"
                          />
                          <span>{contact.address}</span>
                        </div>
                      )}
                      {contact.email && (
                        <a
                          href={`mailto:${contact.email}`}
                          className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm text-gray-600 transition-colors hover:text-blue-600"
                        >
                          <Mail size={17} className="text-blue-600" />
                          {contact.email}
                        </a>
                      )}
                      {contact.phone && (
                        <a
                          href={`tel:${contact.phone}`}
                          className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm text-gray-600 transition-colors hover:text-blue-600"
                        >
                          <Phone size={17} className="text-blue-600" />
                          {contact.phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Container>
          </section>
        </Effects>
      )}

      <div ref={newArrivalsRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`py-12 ${homeContainer}`}
          isLoading={newArrivalsLoading}
        // isVisible={newArrivalsInView}
        >
          <SectionHeading
            id="products-collections"
            subtitle="Fresh arrivals and highlighted collections in one easy place."
            viewMore={ROUTE_PATHS.PRODUCTS}
          >
            New Arrivals
          </SectionHeading>

          <Container
            variant="full"
            showEmptyState={!newArrivalsLoading && !newArrivals.length}
            emptyMessage="No new arrivals to show yet."
          >
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {newArrivals.slice(0, 8).map((product, i) => (
                <ProductCard key={product.id ?? i} product={product} />
              ))}
            </div>
          </Container>
        </Container>
      </Effects>

      <div ref={blogsRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`py-10 ${homeContainer}`}
          isLoading={blogsLoading}
          // isVisible={blogsInView}
          showEmptyState={!blogsLoading && !blogs.length}
          emptyMessage="No blogs to show yet."
        >
          <SectionHeading
            id="latest-blogs"
            subtitle="Discover stories, trends, and expert tips from our team."
            viewMore={ROUTE_PATHS.BLOG}
          >
            Our Latest Blogs
          </SectionHeading>

          {blogs.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
                <div className="lg:col-span-3 lg:row-span-3">
                  <BlogCard
                    title={blogs[0].title}
                    description={blogs[0].description}
                    img={blogs[0].img}
                    hoverImg={blogs[0].hoverImg}
                    href={ROUTE_PATHS.BLOG_DETAIL.replace(
                      ":slug",
                      blogs[0].slug,
                    )}
                    category={blogs[0].category}
                    tall
                  />
                </div>
                {blogs.slice(1, 4).map((blog, i) => (
                  <BlogCard
                    key={i}
                    title={blog.title}
                    description={blog.description}
                    img={blog.img}
                    hoverImg={blog.hoverImg}
                    href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", blog.slug)}
                    category={blog.category}
                  />
                ))}
              </div>

              {blogs.length > 4 && (
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {blogs.slice(4).map((blog, i) => (
                    <BlogCard
                      key={i}
                      title={blog.title}
                      description={blog.description}
                      img={blog.img}
                      hoverImg={blog.hoverImg}
                      href={ROUTE_PATHS.BLOG_DETAIL.replace(":slug", blog.slug)}
                      category={blog.category}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </Container>
      </Effects>

      <Effects type="scroll-fadeup">
        <section className="border-y border-gray-100 bg-gray-50 py-12">
          <Container className={homeContainer}>
            <SectionHeading
              id="testimonials"
              subtitle="Real feedback from customers who value quality, speed, and helpful support."
            >
              Customer Reviews
            </SectionHeading>

            <div className="reviews-slider xl:px-14">
              <Slider
                options={{
                  type: "loop",
                  gap: "1.5rem",
                  autoplay: true,
                  pauseOnHover: true,
                  interval: 5000,
                  perPage: 2,
                  arrows: true,
                  pagination: true,
                  breakpoints: {
                    900: { perPage: 1, arrows: false },
                  },
                }}
              >
                {({ SplideSlide }) => (
                  <>
                    {featuredReviews.map((review, i) => (
                      <SplideSlide key={i}>
                        <ReviewCard review={review} />
                      </SplideSlide>
                    ))}
                  </>
                )}
              </Slider>
            </div>
          </Container>
        </section>
      </Effects>

      <div ref={bannerRef} />
      <Effects type="scroll-fadeup">
        <Container
          className={`py-12 ${homeContainer} min-h-fit`}
          isLoading={bannerLoading}
        // isVisible={bannerInView || !!bannerData}
        >
          {bannerData && (
            <div className="group grid overflow-hidden rounded-2xl border border-gray-200 bg-slate-950 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="flex min-h-[300px] flex-col justify-center p-7 text-white sm:p-10">
                <Text className="mb-3 inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-blue-100">
                  <ShoppingBag size={14} />
                  Featured Collection
                </Text>
                <Text
                  is="h2"
                  className="text-3xl font-black leading-tight md:text-4xl"
                >
                  {bannerData.title}
                </Text>
                {bannerData.short_description && (
                  <Text className="mt-4 max-w-xl text-sm leading-7 text-white/70">
                    {bannerData.short_description}
                  </Text>
                )}
                {bannerData.link && bannerData.button_text && (
                  <a
                    href={bannerData.link}
                    className="mt-7 inline-flex w-fit items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-gray-950 transition-all duration-300 hover:-translate-y-0.5 hover:bg-blue-600 hover:text-white"
                  >
                    {bannerData.button_text}
                    <ArrowRight size={16} />
                  </a>
                )}
              </div>
              {bannerData.image && (
                <div className="relative min-h-[300px] overflow-hidden">
                  <Img
                    src={getImg(bannerData.image)}
                    alt={bannerData.image_alt || bannerData.title}
                    variants={bannerData.image_varients}
                    resolveUrl={(p) => `${banner_image_url}${p}`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 to-transparent lg:bg-gradient-to-r" />
                </div>
              )}
            </div>
          )}
        </Container>
      </Effects>

      <Effects type="scroll-fadeup">
        <Container className={`pb-14 ${homeContainer}`}>
          <div className="grid grid-cols-1 gap-3 rounded-2xl border border-gray-200 bg-white p-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: <Truck size={20} />,
                title: "Free Delivery",
                desc: "Available on eligible orders.",
              },
              {
                icon: <ShieldCheck size={20} />,
                title: "Secure Shopping",
                desc: "Clear product inquiry and support.",
              },
              {
                icon: <CheckCircle size={20} />,
                title: "Quality Checked",
                desc: "Products reviewed before listing.",
              },
              {
                icon: <Phone size={20} />,
                title: "Fast Support",
                desc: "Quick help for product questions.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="flex items-center gap-4 rounded-xl p-4"
              >
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gray-950 text-white">
                  {item.icon}
                </span>
                <span>
                  <Text
                    is="span"
                    className="block text-sm font-bold text-gray-950"
                  >
                    {item.title}
                  </Text>
                  <Text
                    is="span"
                    className="mt-1 block text-xs leading-5 text-gray-500"
                  >
                    {item.desc}
                  </Text>
                </span>
              </div>
            ))}
          </div>
        </Container>
      </Effects>
    </UserLayout>
  );
};
